import { useRef, useLayoutEffect, useCallback, useState, useEffect, useMemo } from 'react';
import { Button, Card, Col, Dropdown, Form, Input, message, Modal, Row, Select, Space, Table, Tooltip, Typography, Divider, Tag, theme } from 'antd';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
// import { CopyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { TABLE_PAGE_SIZE_OPTIONS, filterFormItemLayout, filterFormGridLayout, filterFormItemStyle, computeButtonColLayout } from '@utils/property';
import { GetQueryString } from '@utils/utils';
import { getProjestsListApi, getProjestsGroupListApi, projectMarkAsTesting } from '@services/Selection';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import copy from 'copy-to-clipboard';
import { selectionActions } from '../store/selectionSlice';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '@components/PageHeader/PageHeader';
import { workspaceSelectionDeclarationsPath, workspaceSelectionInfoPath, workspaceSelectionReviewsPath, workspaceSelectionVotePath } from '@/layouts/selectionWorkspacePaths';
import './ProjectsList.less';
import { render } from 'less';
const { Text } = Typography;

const { setProjectFilter: setFilter, setProjectCursor: setCursor } = selectionActions;

const ProjectsList = () => {
    const navigate = useNavigate();
    const { token } = theme.useToken();

    const dispatch = useDispatch();
    const filter = useSelector(state => state.selection.project.filter);
    const cursor = useSelector(state => state.selection.project.cursor);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);

    const [projectsGroupList, setProjectsGroupList] = useState([]);

    const buttonColLayout = computeButtonColLayout(4);

    // fixed-scroll 三件套：表格高度自适应 + 横向滚动守卫 + 纵向阴影
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleRefresh = useCallback(() => {
        getData();
    }, [filter]);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const onFinish = values => {
        dispatch(
            setFilter({
                ...filter,
                ...values,
                page: 1,
            }),
        );
    };

    const onReset = () => {
        form.resetFields();

        dispatch(
            setFilter({
                ...filter,
                ...form.getFieldsValue(),
                page: 1,
            }),
        );
    };

    const onChangePage = (page, pageSize) => {
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };

    const onShowSizeChange = (page, pageSize) => onChangePage(1, pageSize);

    const filterOption = (input, option) => {
        const { groupName } = option.props.data;
        return groupName.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    };

    useEffect(() => {
        //获取评议项目组
        getProjestsGroupListApi().then(res => {
            if (res && res.status_code === 200) {
                setProjectsGroupList(res.data);
            }
        });
    }, []);

    useEffect(() => {
        document.title = '评议项目管理-医评议';
    }, []);

    // 医项目跳转携带参数
    useEffect(() => {
        const projectName = GetQueryString('projectName');
        if (projectName) {
            form.setFieldValue('name', unescape(projectName));
            const timer = setTimeout(() => {
                getData(unescape(projectName));
                clearTimeout(timer);
            }, 500);
        }
    }, []);

    const getData = async projectName => {
        try {
            setLoading(true);
            const params = {
                ...filter,
                limit: filter.pageSize,
            };
            // console.log(params);

            if (projectName) {
                params.name = projectName;
            }

            const res = await getProjestsListApi(params);
            if (res?.status_code === 200) {
                setData(res.data?.list);
                setTotal(res.data?.totalCount);
            }
            setLoading(false);
        } catch (e) {
            setLoading(false);
        }
    };

    useEffect(() => {
        form.setFieldsValue(filter);

        getData();
    }, [filter]);

    const copyClick = mes => {
        copy(mes);
        message.success('已复制到剪贴板');
    };

    const columns = [
        {
            title: '氚云编号',
            dataIndex: 'h3yunNo',
            width: 150,
            render: data => <span>{data || <Text type="secondary">&ndash;</Text>}</span>,
        },
        {
            title: '项目ID',
            dataIndex: 'id',
            width: 120,
            render: data => {
                return (
                    <span className="conferenctlist-copy-column-span">
                        {'YPX' + ('000000' + data).slice(-6)}
                        <span onClick={() => copyClick('YPX' + ('000000' + data).slice(-6))}>
                            <i className="bi bi-copy" />
                        </span>
                    </span>
                );
            },
        },
        {
            title: '项目名称',
            dataIndex: 'name',
            width: 250,
            render: (_, row) => (
                <>
                    {row.name}
                    {!!row.isTesting && (
                        <Tag color="#f50" style={{ marginLeft: 4, padding: '0 4px' }}>
                            TEST
                        </Tag>
                    )}
                </>
            ),
        },
        {
            title: '项目组',
            dataIndex: 'projectGroup',
            width: 150,
            render: (_, row) => {
                return projectsGroupList?.filter(item => item.id === row?.groupId)[0]?.groupName;
            },
        },
        {
            title: '起止日期',
            dataIndex: 'data',
            width: 200,
            render: (_, row) => {
                return `${dayjs(row.startedAt).format('YYYY-MM-DD')} 至 ${dayjs(row.endedAt).format('YYYY-MM-DD')}`;
            },
        },
        {
            title: '创建信息',
            dataIndex: 'createdAt',
            width: 150,
            render: (_, row) => (
                <>
                    {row.createdName && (
                        <>
                            {row.createdName}
                            <br />
                        </>
                    )}
                    <Text type="secondary">{row.createdAt}</Text>
                </>
            ),
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 280,
            fixed: 'right',
            render: (_, record) => {
                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button size="small" type="link" onClick={() => gotoProjectWorkspace(record, workspaceSelectionDeclarationsPath(record.id))}>
                            申报管理
                        </Button>
                        <Button size="small" type="link" onClick={() => gotoProjectWorkspace(record, workspaceSelectionVotePath(record.id))}>
                            投票管理
                        </Button>
                        <Button size="small" type="link" onClick={() => gotoProjectWorkspace(record, workspaceSelectionReviewsPath(record.id))}>
                            评议管理
                        </Button>
                        <Button size="small" type="link" onClick={() => gotoProjectWorkspace(record, workspaceSelectionInfoPath(record.id))}>
                            编辑项目信息
                        </Button>
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'toggle-testing',
                                        label: !!record.isTesting ? '标记为正式项目' : '标记为测试项目',
                                    },
                                ],
                                onClick: ({ key }) => {
                                    if (key === 'toggle-testing') {
                                        markAsTesting(record);
                                    }
                                },
                            }}
                            placement="bottomRight"
                        >
                            <Button type="link" size="small" className="more-operation">
                                <i className="bi bi-three-dots" />
                            </Button>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    const markAsTesting = record => {
        Modal.confirm({
            title: '标记为测试项目',
            icon: <i className="bi bi-exclamation-circle" />,
            content: '医疗决策人数据统计等数据指标将排除标记为测试的评议项目，确定标记为测试项目？',
            okText: '确认',
            cancelText: '取消',
            centered: true,
            onOk: () => {
                projectMarkAsTesting({
                    id: record.id,
                    isTesting: !!record.isTesting ? 0 : 1,
                }).then(res => {
                    if (res.status_code === 200) {
                        const msg = !!record.isTesting ? '标记为正式项目成功' : '标记为测试项目成功';
                        message.success(msg);

                        getData();
                    }
                });
            },
        });
    };

    const gotoProjectWorkspace = (record, path) => {
        dispatch(setCursor(record.id));
        navigate(path, { state: { projectName: record.name } });
    };

    return (
        <div className="selection-projects-page">
            <PageHeader title="评议项目" breadcrumb={[{ label: '医评议' }, { label: '评议项目' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <Card
                    size="small"
                    className="selection-projects-filter-panel"
                    style={{
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillAlter,
                    }}
                >
                    <Form {...filterFormItemLayout} labelCol={{ md: { span: 7 } }} onFinish={onFinish} form={form} autoComplete="off" requiredMark={false}>
                        <Row gutter={[16, 16]}>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="项目名称" name="name" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="项目名称关键字" autoComplete="off" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="评议项目组" name="groupId" style={filterFormItemStyle}>
                                    <Select placeholder="请选择评议项目组" showSearch={{ optionFilterProp: 'children', filterOption: filterOption }} allowClear>
                                        {projectsGroupList
                                            && projectsGroupList.map(item => (
                                                <Select.Option key={item.id} value={item.id} data={item}>
                                                    {item.groupName}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="正式/测试" name="isTesting" style={filterFormItemStyle}>
                                    <Select placeholder="选择是否测试项目" allowClear>
                                        <Select.Option value={0}>正式项目</Select.Option>
                                        <Select.Option value={1}>测试项目</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="氚云编号" name="h3yunNo" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="氚云项目编号" autoComplete="off" />
                                </Form.Item>
                            </Col>

                            <Col {...buttonColLayout}>
                                <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                                    <Space size="small">
                                        <Button type="primary" htmlType="submit" icon={<i className="bi bi-search" aria-hidden="true" />}>
                                            检索
                                        </Button>
                                        <Button type="default" onClick={onReset}>
                                            重置
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container selection-projects-table-region">
                <div className="main-toolbar selection-projects-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={() => navigate(`/selection/projects/add`)}>
                                新增项目
                            </Button>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap selection-projects-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always projects-table' : 'projects-table'}
                        rowKey={item => item.id}
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        bordered
                        sticky
                        size="medium"
                        rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                        pagination={{
                            total: total,
                            showTotal: () => `共${total}条`,
                            current: filter.page,
                            pageSize: filter.pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                            onChange: onChangePage,
                            onShowSizeChange,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ProjectsList;
